import 'bootstrap/dist/css/bootstrap.min.css';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  API_ADD_USER,
  API_DELETE_USER,
  API_EMPLOYEES,
  API_MODULE_ACCESS_CATALOG,
  API_UPDATE_USER,
  API_UPDATE_USER_STATUS,
  API_USERS,
} from '../Config/ApiUrlConstant';
import { useAuth } from '../AuthContext';
import { apiJson } from '../utils/apiClient';
import { confirmAction } from '../utils/confirmAction';
import './UserMgmt.css';

const roleOptions = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'employee', label: 'Employee' },
];

const permissionGroups = [
  { label: 'Disbursement Management', keys: ['disbursement', 'disbursement_queue', 'disbursement_history'] },
  { label: 'Reports', keys: ['repayment_report', 'advance_repayment_report', 'operations_report', 'od_report'] },
];

const createEmptyPermissionMap = (modules) =>
  modules.map((moduleItem) => ({
    module_key: moduleItem.key,
    can_view: moduleItem.actions.includes('view') ? 0 : 0,
    can_create: moduleItem.actions.includes('create') ? 0 : 0,
    can_edit: moduleItem.actions.includes('edit') ? 0 : 0,
    can_delete: moduleItem.actions.includes('delete') ? 0 : 0,
  }));

const buildDefaultPermissionsForRole = (roleKey, modules) => {
  if (roleKey === 'super_admin') {
    return modules.map((moduleItem) => ({
      module_key: moduleItem.key,
      can_view: moduleItem.actions.includes('view') ? 1 : 0,
      can_create: moduleItem.actions.includes('create') ? 1 : 0,
      can_edit: moduleItem.actions.includes('edit') ? 1 : 0,
      can_delete: moduleItem.actions.includes('delete') ? 1 : 0,
    }));
  }

  if (roleKey === 'admin') {
    return modules.map((moduleItem) => ({
      module_key: moduleItem.key,
      can_view: moduleItem.key !== 'user_management' && moduleItem.actions.includes('view') ? 1 : 0,
      can_create: moduleItem.key !== 'user_management' && moduleItem.actions.includes('create') ? 1 : 0,
      can_edit: moduleItem.key !== 'user_management' && moduleItem.actions.includes('edit') ? 1 : 0,
      can_delete: moduleItem.key !== 'user_management' && moduleItem.actions.includes('delete') ? 1 : 0,
    }));
  }

  if (roleKey === 'manager') {
    return modules.map((moduleItem) => {
      const enabledModules = ['dashboard', 'centers', 'employees', 'clients', 'disbursement'];
      const isEnabled = enabledModules.includes(moduleItem.key);
      return {
        module_key: moduleItem.key,
        can_view: isEnabled && moduleItem.actions.includes('view') ? 1 : 0,
        can_create: isEnabled && moduleItem.actions.includes('create') ? 1 : 0,
        can_edit: isEnabled && moduleItem.actions.includes('edit') ? 1 : 0,
        can_delete: isEnabled && moduleItem.actions.includes('delete') ? 1 : 0,
      };
    });
  }

  return modules.map((moduleItem) => ({
    module_key: moduleItem.key,
    can_view: moduleItem.key === 'dashboard' && moduleItem.actions.includes('view') ? 1 : 0,
    can_create: 0,
    can_edit: 0,
    can_delete: 0,
  }));
};

const UserManagement = () => {
  const { user, canAccess, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedEmployeeId = searchParams.get('employee_id');
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [message, setMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email_id: '',
    username: '',
    password: '',
    role_key: 'admin',
    employee_id: '',
    status: 1,
    permissions: [],
  });

  const canCreateUsers = canAccess('user_management', 'create');
  const canEditUsers = canAccess('user_management', 'edit');
  const canDeleteUsers = canAccess('user_management', 'delete');
  const currentUserId = String(user?.id || '');

  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage('');
      const [{ data: moduleData }, { data: userData }, { data: employeeData }] = await Promise.all([
        apiJson(API_MODULE_ACCESS_CATALOG, { method: 'POST', body: JSON.stringify({}) }),
        apiJson(API_USERS, { method: 'POST', body: JSON.stringify({}) }),
        apiJson(API_EMPLOYEES, { method: 'POST', body: JSON.stringify({}) }),
      ]);

      const moduleList = moduleData?.data || [];
      const employeeList = employeeData?.data || [];
      const userList = userData?.data || [];
      const requestedEmployee = employeeList.find(
        (employee) => String(employee.id) === requestedEmployeeId
      );
      const linkedUser = requestedEmployee
        ? userList.find((listedUser) => String(listedUser.employee_id) === String(requestedEmployee.id))
        : null;
      setModules(moduleList);
      setUsers(userList);
      setEmployees(employeeList);
      if (linkedUser) {
        setSelectedUserId(linkedUser.id);
        setFormData({
          name: linkedUser.name || requestedEmployee?.emp_name || '',
          email_id: linkedUser.email_id || '',
          username: linkedUser.username || '',
          password: '',
          role_key: linkedUser.role_key || 'employee',
          employee_id: String(requestedEmployee.id),
          status: Number(linkedUser.status ?? 1),
          permissions: linkedUser.permissions?.length
            ? linkedUser.permissions
            : createEmptyPermissionMap(moduleList),
        });
      } else {
        setFormData((current) => ({
          ...current,
          ...(requestedEmployee ? {
            employee_id: String(requestedEmployee.id),
            name: requestedEmployee.emp_name || current.name,
          } : {}),
          permissions: current.permissions.length ? current.permissions : buildDefaultPermissionsForRole('admin', moduleList),
        }));
      }
    } catch (error) {
      setErrorMessage('Unable to load user management data right now.');
    } finally {
      setIsLoading(false);
    }
  }, [requestedEmployeeId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const filteredUsers = useMemo(() => users, [users]);
  const availableEmployees = useMemo(() => employees.filter((employee) => (
    !employee.linked_user_id || String(employee.linked_user_id) === String(selectedUserId || '')
  )), [employees, selectedUserId]);
  const permissionSections = useMemo(() => {
    const groupedKeys = new Set(permissionGroups.flatMap((group) => group.keys));
    const sections = permissionGroups.map((group) => ({
      label: group.label,
      modules: group.keys.map((key) => modules.find((moduleItem) => moduleItem.key === key)).filter(Boolean),
    })).filter((section) => section.modules.length);
    const remaining = modules.filter((moduleItem) => !groupedKeys.has(moduleItem.key));
    return remaining.length ? [{ label: 'Other Modules', modules: remaining }, ...sections] : sections;
  }, [modules]);

  const resetForm = () => {
    setSelectedUserId(null);
    setFormData({
      name: '',
      email_id: '',
      username: '',
      password: '',
      role_key: 'admin',
      employee_id: '',
      status: 1,
      permissions: modules.length ? buildDefaultPermissionsForRole('admin', modules) : [],
    });
  };

  const handleRoleChange = (event) => {
    const nextRole = event.target.value;
    setFormData((current) => ({
      ...current,
      role_key: nextRole,
      permissions: buildDefaultPermissionsForRole(nextRole, modules),
    }));
  };

  const handleFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((current) => ({
      ...current,
      [name]: name === 'status' ? Number(value) : value,
    }));
  };

  const handleEmployeeChange = (event) => {
    const employeeId = event.target.value;
    const selectedEmployee = employees.find((employee) => String(employee.id) === employeeId);

    setFormData((current) => ({
      ...current,
      employee_id: employeeId,
      name: selectedEmployee?.emp_name || current.name,
    }));
  };

  const handlePermissionChange = (moduleKey, permissionKey, checked) => {
    setFormData((current) => ({
      ...current,
      permissions: current.permissions.map((permission) => {
        if (permission.module_key !== moduleKey) {
          return permission;
        }

        const nextPermission = {
          ...permission,
          [permissionKey]: checked ? 1 : 0,
        };

        if (permissionKey !== 'can_view' && checked) {
          nextPermission.can_view = 1;
        }

        if (permissionKey === 'can_view' && !checked) {
          nextPermission.can_create = 0;
          nextPermission.can_edit = 0;
          nextPermission.can_delete = 0;
        }

        return nextPermission;
      }),
    }));
  };

  const handleEditUser = (selectedUser) => {
    if (String(selectedUser.id) === currentUserId) {
      window.alert('The logged-in super admin account cannot be modified from this screen.');
      return;
    }

    setSelectedUserId(selectedUser.id);
    setFormData({
      name: selectedUser.name || '',
      email_id: selectedUser.email_id || '',
      username: selectedUser.username || '',
      password: '',
      role_key: selectedUser.role_key || 'employee',
      employee_id: selectedUser.employee_id ? String(selectedUser.employee_id) : '',
      status: Number(selectedUser.status ?? 1),
      permissions: selectedUser.permissions?.length
        ? selectedUser.permissions
        : createEmptyPermissionMap(modules),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteUser = async (userId) => {
    if (String(userId) === currentUserId) {
      window.alert('The logged-in super admin account cannot be deleted.');
      return;
    }

    if (!confirmAction('archive this user')) {
      return;
    }

    try {
      const { data } = await apiJson(API_DELETE_USER, {
        method: 'POST',
        body: JSON.stringify({ user_id: userId }),
      });
      setMessage(data?.message || 'User archived successfully.');
      await loadData();
      if (selectedUserId === userId) {
        resetForm();
      }
    } catch (error) {
      setErrorMessage('Unable to delete user.');
    }
  };

  const handleToggleStatus = async (selectedUser) => {
    if (String(selectedUser.id) === currentUserId) {
      window.alert('The logged-in super admin account cannot be disabled.');
      return;
    }

    if (!confirmAction('change this user status')) {
      return;
    }

    try {
      const { data } = await apiJson(API_UPDATE_USER_STATUS, {
        method: 'POST',
        body: JSON.stringify({
          user_id: selectedUser.id,
          status: Number(selectedUser.status) === 1 ? 0 : 1,
        }),
      });
      setMessage(data?.message || 'User status updated successfully.');
      await loadData();
    } catch (error) {
      setErrorMessage('Unable to update user status.');
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!confirmAction(selectedUserId ? 'update this user' : 'create this user')) return;

    try {
      setIsSubmitting(true);
      setMessage('');
      setErrorMessage('');

      const url = selectedUserId ? API_UPDATE_USER : API_ADD_USER;
      const payload = {
        ...formData,
        permissions: formData.permissions,
      };

      if (selectedUserId) {
        payload.user_id = selectedUserId;
      }

      const { data } = await apiJson(url, {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      setMessage(data?.message || (selectedUserId ? 'User updated successfully.' : 'User created successfully.'));
      resetForm();
      await loadData();
    } catch (error) {
      setErrorMessage('Unable to save user. Please verify the username and permissions.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return <div className="container mt-5">Loading user access...</div>;
  }

  if (!canAccess('user_management', 'view')) {
    return <div className="container mt-5">Only super admin can manage users.</div>;
  }

  return (
    <div className="user-page">
      <section className="employee-hero">
        <div>
          <p className="employee-eyebrow">Access Control</p>
          <h1>User Management</h1>
          <p className="employee-subtitle">
            Create super admin, admin, manager, and employee logins with dynamic module-level access.
          </p>
        </div>
        <div className="employee-hero-note">
          <strong>{users.length}</strong>
          <span>banking users configured</span>
        </div>
      </section>

      {message ? <div className="employee-banner employee-banner-success">{message}</div> : null}
      {errorMessage ? <div className="employee-banner employee-banner-error">{errorMessage}</div> : null}

      <section className="employee-layout">
        <article className="employee-card">
          <div className="employee-section-header">
            <div>
              <h2>{selectedUserId ? 'Edit User' : 'Create User'}</h2>
              <p>Role selection sets a starting permission set, and you can adjust each module before saving.</p>
            </div>
          </div>

          <form className="employee-form" onSubmit={handleSubmit}>
            <div className="employee-form-grid">
              <div>
                <label htmlFor="name">Full Name</label>
                <input id="name" name="name" value={formData.name} onChange={handleFieldChange} />
              </div>

              <div>
                <label htmlFor="email_id">Email</label>
                <input id="email_id" name="email_id" value={formData.email_id} onChange={handleFieldChange} />
              </div>

              <div>
                <label htmlFor="username">Username</label>
                <input id="username" name="username" value={formData.username} onChange={handleFieldChange} required />
              </div>

              <div>
                <label htmlFor="password">{selectedUserId ? 'New Password' : 'Password'}</label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleFieldChange}
                  required={!selectedUserId}
                />
              </div>

              <div>
                <label htmlFor="role_key">Role</label>
                <select id="role_key" name="role_key" value={formData.role_key} onChange={handleRoleChange} required>
                  {roleOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {formData.role_key !== 'super_admin' ? (
                <div>
                  <label htmlFor="employee_id">Linked Employee</label>
                  <select id="employee_id" name="employee_id" value={formData.employee_id} onChange={handleEmployeeChange} required>
                    <option value="">Select employee and assigned branch</option>
                    {availableEmployees.map((employee) => (
                      <option key={employee.id} value={employee.id}>
                        {employee.emp_generate_id} - {employee.emp_name} ({employee.branch_name || 'No branch'})
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              <div>
                <label htmlFor="status">Status</label>
                <select id="status" name="status" value={formData.status} onChange={handleFieldChange}>
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>
            </div>

            <div className="user-permission-sections">
              {permissionSections.map((section) => <section key={section.label} className="user-permission-section">
                <h3>{section.label}</h3>
                <div className="user-permission-grid">
              {section.modules.map((moduleItem) => {
                const currentPermission = formData.permissions.find((permission) => permission.module_key === moduleItem.key) || {
                  module_key: moduleItem.key,
                  can_view: 0,
                  can_create: 0,
                  can_edit: 0,
                  can_delete: 0,
                };

                return (
                  <div key={moduleItem.key} className="user-permission-card">
                    <strong>{moduleItem.label}</strong>
                    <div className="user-permission-actions">
                      {moduleItem.actions.map((action) => {
                        const permissionKey = `can_${action}`;
                        return (
                          <label key={permissionKey}>
                            <input
                              type="checkbox"
                              checked={Boolean(currentPermission[permissionKey])}
                              onChange={(event) => handlePermissionChange(moduleItem.key, permissionKey, event.target.checked)}
                            />
                            <span>{action}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
                </div>
              </section>)}
            </div>

            <div className="employee-actions">
              <button type="submit" className="btn btn-primary" disabled={isSubmitting || (!selectedUserId && !canCreateUsers) || (selectedUserId && !canEditUsers)}>
                {isSubmitting ? 'Saving...' : selectedUserId ? 'Update User' : 'Create User'}
              </button>
              <button type="button" className="btn btn-outline-secondary" onClick={resetForm}>
                Reset
              </button>
            </div>
          </form>
        </article>

        <article className="employee-card">
          <div className="employee-section-header">
            <div>
              <h2>User Directory</h2>
              <p>Review user roles, current status, and edit dynamic module access when needed.</p>
            </div>
          </div>

          {isLoading ? (
            <div className="dashboard-empty-state">Loading users...</div>
          ) : (
            <div className="employee-table-wrap">
              <table className="employee-table data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Username</th>
                    <th>Role</th>
                    <th>Employee / Branch</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((listedUser) => (
                    <tr key={listedUser.id}>
                      <td>{listedUser.name || 'N/A'}</td>
                      <td>{listedUser.username}</td>
                      <td>{listedUser.role_key}</td>
                      <td>{listedUser.employee_code ? `${listedUser.employee_code} - ${listedUser.employee_branch_name || 'No branch'}` : 'Not linked'}</td>
                      <td>{Number(listedUser.status) === 1 ? 'Active' : 'Inactive'}</td>
                      <td>
                        <div className="user-table-actions">
                          <button type="button" className="btn btn-sm btn-warning" onClick={() => handleEditUser(listedUser)} disabled={!canEditUsers || String(listedUser.id) === currentUserId}>
                            Edit
                          </button>
                          <button type="button" className="btn btn-sm btn-secondary" onClick={() => handleToggleStatus(listedUser)} disabled={!canEditUsers || String(listedUser.id) === currentUserId}>
                            {Number(listedUser.status) === 1 ? 'Disable' : 'Enable'}
                          </button>
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => handleDeleteUser(listedUser.id)} disabled={!canDeleteUsers || String(listedUser.id) === currentUserId}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </article>
      </section>
    </div>
  );
};

export default UserManagement;
